import React, { useEffect } from 'react';
import { Card, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import BookingStateHook from '@/hooks/useBookingState';
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks';
import { setSearch } from '@/AppRedux/actions/booking';
import { getEffectiveMinSelectableDate } from '@/utils/bookingCutoff';
import { useBookingSearchAction } from './useBookingSearchAction';
import BookingSearchFormVersion2 from './BookingSearchForm';
import BookingCutoffModal from '../Modals/BookingCutoffModal';
import { BookingSearchCardProps } from '.';

const BookingSearchCardDesktop: React.FC<BookingSearchCardProps> = ({
  mode,
  propcode,
  onSearchOverride,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { search, loading } = BookingStateHook();
  const { token } = theme.useToken();
  const hotelSettings = useAppSelector((state) => state.hotelProfile.hotelSettings);
  const {
    updateLiveSearch,
    handleDateChange,
    handleAdultsChange,
    handleChildrenChange,
    handleInfantsChange,
    triggerSearch,
    isCutoffModalOpen,
    closeCutoffModal,
    confirmSearchTomorrow,
  } = useBookingSearchAction({ propcode, onSearchOverride, hotelSettings });

  const minSelectableDate = getEffectiveMinSelectableDate(hotelSettings);

  useEffect(() => {
    if (!search.checkIn || !search.checkOut) {
      updateLiveSearch({
        checkIn: minSelectableDate.format('YYYY-MM-DD'),
        checkOut: minSelectableDate.add(1, 'day').format('YYYY-MM-DD'),
      });
    }
  }, [updateLiveSearch, search.checkIn, search.checkOut, minSelectableDate]);

  // Track propCode changes — reset guestTouched so new hotel's defaults can apply
  useEffect(() => {
    if (!propcode) return;
    if (search.lastPropCode === propcode) return;
    dispatch(setSearch({
      lastPropCode: propcode,
      ...(search.lastPropCode !== null && { guestTouched: false }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propcode, dispatch]);

  // Apply settings defaults when not touched by user
  useEffect(() => {
    if (search.guestTouched) return;
    if (!hotelSettings?.default_search_guests) return;
    dispatch(setSearch({
      adults: hotelSettings.default_search_guests.adult ?? 2,
      children: hotelSettings.default_search_guests.child ?? 0,
      infants: hotelSettings.default_search_guests.infant ?? 0,
    }));
  }, [hotelSettings?.default_search_guests, search.guestTouched, dispatch]);


  const onTrigger = () => {
    triggerSearch(search);
  }

  return (
    <>
    <Card
      variant='outlined'
      style={{
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        borderRadius: token.borderRadiusLG,
        pointerEvents: 'auto'
      }}
      styles={{ body: { padding: mode === "mobile" ? 16 : 24  } }}
    >
      <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
        {intl.formatMessage({
          id: 'booking.searchTitle',
          defaultMessage: 'Find your room',
        })}
      </Typography.Title>

      <BookingSearchFormVersion2
        dates={
          search.checkIn && search.checkOut
            ? [dayjs(search.checkIn), dayjs(search.checkOut)]
            : [minSelectableDate, minSelectableDate.add(1, "day")]
        }
        minSelectableDate={minSelectableDate}
        adults={search.adults ?? 2}
        children={search.children ?? 0}
        infants={search.infants ?? 0}
        onDatesChange={handleDateChange}
        onAdultsChange={handleAdultsChange}
        onChildrenChange={handleChildrenChange}
        onInfantsChange={handleInfantsChange}
        onTrigger={onTrigger}
        loading={loading}
      />
    </Card>
    <BookingCutoffModal
      isOpen={isCutoffModalOpen}
      cutoffTime={hotelSettings?.booking_cutoff_time}
      onClose={closeCutoffModal}
      onSearchTomorrow={confirmSearchTomorrow}
    />
    </>
  );
};

export default BookingSearchCardDesktop;
